import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, Filter } from 'lucide-react';
import { Button } from './ui/button';
import { useEffect, useState } from 'react';
import { CallAPIPromise } from '../routeBilling/menu/dashboard/biiling/modal/comman';
import { useDispatch, useSelector } from 'react-redux';
import { selectPrefix, selectShopDetails } from '../utility/Redux/profile';
import moment from 'moment';
import { setSpinnerLoading } from '../utility/Redux/modal';
import SpinnerLoading from '../utility/component/modals/spinnerLoading';

export default function Ledger() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [filteredRows, setFilteredRows] = useState([]);

  const prefix = useSelector(selectPrefix);
  const shopDetails = useSelector(selectShopDetails);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setSpinnerLoading("Loading"));
    CallAPIPromise("/api/shopBalance/partyLedger", {
      table_prefix: prefix,
      ACCOUNT_NO: shopDetails?.account_no,
      from_date: moment().subtract(8, "month").format("YYYY-MM-DD"),
      to_date: moment().format("YYYY-MM-DD"),
    })
      .then((e) => {
        if (!e.data.error) {
          let list = [];
          const data = e.data.response;
          let op_bal = 0;

          for (let i = 0; i < data.length; i++) {
            list.push({
              ...data[i],
              id: i + 1,
              OP_BALANCE: op_bal,
              BALANCE: op_bal + (Number(data[i].DEBIT) - Number(data[i].CREDIT)),
            });

            op_bal = op_bal + (Number(data[i].DEBIT) - Number(data[i].CREDIT));
          }

          setRows(list);
        }
      })
      .finally(() => {
        dispatch(setSpinnerLoading(false));
      });
  }, []);

  // ==========================
  // FILTER LOGIC
  // ==========================
  useEffect(() => {
    let list = [...rows];

    // Search filter
    if (search.trim() !== "") {
      const s = search.toLowerCase();
      list = list.filter(
        (item) =>
          item.DESC_NAME?.toLowerCase().includes(s) ||
          item.NAME?.toLowerCase().includes(s) ||
          item.id?.toString().includes(s)
      );
    }

    // Type filter
    if (typeFilter === "credit") {
      list = list.filter((item) => Number(item.CREDIT) > 0);
    } else if (typeFilter === "debit") {
      list = list.filter((item) => Number(item.DEBIT) > 0);
    }

    setFilteredRows(list);
  }, [rows, search, typeFilter]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-gray-900 mb-1">Ledger</h2>
        <p className="text-gray-600">Complete transaction history</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Box */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search transactions..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Transactions</SelectItem>
                <SelectItem value="credit">Credit Only</SelectItem>
                <SelectItem value="debit">Debit Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Ledger Table - Desktop */}
      <Card className="hidden md:block overflow-hidden">
        <CardHeader>
          <CardTitle className="text-gray-900">Transaction History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-gray-700">Date</th>
                  <th className="px-6 py-3 text-left text-gray-700">Transaction ID</th>
                  <th className="px-6 py-3 text-left text-gray-700">Description</th>
                  <th className="px-6 py-3 text-left text-gray-700">Party Name</th>
                  <th className="px-6 py-3 text-right text-gray-700">Debit</th>
                  <th className="px-6 py-3 text-right text-gray-700">Credit</th>
                  <th className="px-6 py-3 text-right text-gray-700">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredRows.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-gray-900">{entry.DATE}</td>
                    <td className="px-6 py-4 text-gray-900">{entry.id}</td>
                    <td className="px-6 py-4 text-gray-900">{entry.DESC_NAME}</td>
                    <td className="px-6 py-4 text-gray-900">{entry.NAME}</td>

                    <td className="px-6 py-4 text-right text-red-600">
                      {entry.DEBIT > 0
                        ? `₹${entry.DEBIT.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                        : "-"}
                    </td>

                    <td className="px-6 py-4 text-right text-green-600">
                      {entry.CREDIT > 0
                        ? `₹${entry.CREDIT.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                        : "-"}
                    </td>

                    <td className="px-6 py-4 text-right text-gray-900">
                      ₹{entry.BALANCE.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Ledger Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {filteredRows.map((entry) => (
          <Card key={entry.id}>
            <CardContent className="pt-6">
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-gray-900">{entry.DESC_NAME}</p>
                    <p className="text-gray-500">{entry.NAME}</p>
                  </div>
                  <span className="text-gray-500">{entry.id}</span>
                </div>

                <div className="flex items-center justify-between text-gray-600 border-t pt-3">
                  <span>{entry.DATE}</span>
                </div>

                <div className="grid grid-cols-2 gap-4 border-t pt-3">
                  <div>
                    <p className="text-gray-600">Debit</p>
                    <p className="text-red-600">
                      {entry.DEBIT > 0
                        ? `₹${entry.DEBIT.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                        : "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-gray-600">Credit</p>
                    <p className="text-green-600">
                      {entry.CREDIT > 0
                        ? `₹${entry.CREDIT.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                        : "-"}
                    </p>
                  </div>
                </div>

                <div className="border-t pt-3">
                  <p className="text-gray-600">Balance</p>
                  <p className="text-gray-900">
                    ₹{entry.BALANCE.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        <SpinnerLoading />
      </div>
    </div>
  );
}
